import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../../services/api';

interface SaleDetail {
  sale_id: number;
  customer_name: string;
  product_name: string;
  vendor_name: string;
  total_amount: number;
  date: string;
  is_credit: boolean;
}

interface Props {
  open: boolean;
  month: string | null;
  label: string;
  filter: 'all' | 'credits' | 'products';
  onClose: () => void;
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
}

export default function MonthSalesDetailDialog({ open, month, label, filter, onClose }: Props) {
  const [sales, setSales] = useState<SaleDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !month) return;
    setLoading(true);
    setError(null);
    api.get('/finance/month-sales-detail', { params: { month, filter } })
      .then((res) => setSales(res.data?.sales || []))
      .catch((err) => setError(err?.response?.data?.detail || 'Error al cargar el detalle'))
      .finally(() => setLoading(false));
  }, [open, month, filter]);

  const productSummary = React.useMemo(() => {
    const map = new Map<string, { count: number; total: number }>();
    for (const s of sales) {
      const entry = map.get(s.product_name) || { count: 0, total: 0 };
      entry.count += 1;
      entry.total += s.total_amount;
      map.set(s.product_name, entry);
    }
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [sales]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Detalle de Ventas — {label}</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Productos vendidos
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {productSummary.length === 0 && (
                <Typography variant="body2" color="text.secondary">Sin ventas este mes.</Typography>
              )}
              {productSummary.map((p) => (
                <Chip
                  key={p.name}
                  label={`${p.name} · ${p.count} (${fmtCurrency(p.total)})`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Detalle por venta ({sales.length})
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Vendedor</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Fecha</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.map((s) => (
                    <TableRow key={s.sale_id} hover>
                      <TableCell>{s.product_name}</TableCell>
                      <TableCell>{s.customer_name}</TableCell>
                      <TableCell>{s.vendor_name}</TableCell>
                      <TableCell align="right">{fmtCurrency(s.total_amount)}</TableCell>
                      <TableCell>{new Date(s.date).toLocaleDateString('es-AR')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
